import { useState } from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blog/ui";

import { t } from "../lib/i18n";
import { useApi } from "../app/ApiContext";
import { PageHeader } from "../app/Shell";
import { useAsync, useSite } from "../lib/hooks";

/**
 * Theme management (TASK-806, TASK-705).
 *
 * The MVP ships exactly one theme, and this page still routes everything
 * through the API's installed-themes list — installing a second theme is data
 * to this page, not a rewrite.
 *
 * Saving commits the selection into the content repository's `config/site.json`
 * (`config: update site settings`), which triggers the blog rebuild. The
 * revision shown here is the commit the next build reads.
 */
export function ThemeSettings() {
  const client = useApi();
  const { site, loading: siteLoading } = useSite(client);
  const siteId = site?.id;

  const settings = useAsync(async () => {
    if (siteId === undefined) {
      return null;
    }

    return client.getTheme(siteId);
  }, [siteId]);

  const [selected, setSelected] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (siteLoading || settings.loading) {
    return <p className="text-sm text-neutral-500">{t("common.loading")}</p>;
  }

  if (settings.error !== null) {
    return (
      <Card>
        <CardContent>
          {t("themes.loadFailed")}: {settings.error}
        </CardContent>
      </Card>
    );
  }

  const view = settings.data;

  if (view === null || siteId === undefined) {
    return (
      <Card>
        <CardContent>{t("themes.noSite")}</CardContent>
      </Card>
    );
  }

  const currentKey = `${view.current.themeId}@${view.current.themeVersion}`;
  const selectedKey = selected ?? currentKey;

  async function save(): Promise<void> {
    if (siteId === undefined) {
      return;
    }

    const [themeId, themeVersion] = selectedKey.split("@");

    if (themeId === undefined || themeVersion === undefined) {
      return;
    }

    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      const result = await client.selectTheme(siteId, { themeId, themeVersion });

      setMessage(
        result.revision === null
          ? t("themes.setNoCommit", { theme: `${themeId}@${themeVersion}` })
          : t("themes.setCommit", {
              theme: `${themeId}@${themeVersion}`,
              sha: result.revision.slice(0, 7),
            }),
      );
      settings.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Saving the theme failed.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <PageHeader title={t("themes.title")} description={t("themes.desc")} />

      <Card>
        <CardHeader>
          <CardTitle>{t("themes.installed")}</CardTitle>
          <CardDescription>
            {t("themes.current", {
              selection: `${view.current.themeId}@${view.current.themeVersion}`,
            })}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <ul
            className="flex flex-col gap-2"
            role="radiogroup"
            aria-label={t("themes.installed")}
          >
            {view.installed.map((theme) => {
              const key = `${theme.id}@${theme.version}`;

              return (
                <li key={key}>
                  <label className="flex cursor-pointer items-center gap-3 rounded-md border border-neutral-200 px-3 py-2 hover:bg-neutral-50">
                    <input
                      type="radio"
                      name="theme"
                      value={key}
                      checked={selectedKey === key}
                      onChange={() => {
                        setSelected(key);
                        setMessage(null);
                        setError(null);
                      }}
                    />
                    <span className="text-sm font-medium text-neutral-900">{theme.name}</span>
                    <span className="text-xs text-neutral-500">{key}</span>
                    {currentKey === key ? <Badge>{t("themes.active")}</Badge> : null}
                  </label>
                </li>
              );
            })}
          </ul>

          <div className="mt-4 flex items-center gap-3">
            <Button onClick={() => void save()} disabled={saving || selectedKey === currentKey}>
              {saving ? t("themes.saving") : t("themes.save")}
            </Button>
            {message !== null && <p className="text-sm text-neutral-600">{message}</p>}
            {error !== null && <p className="text-sm text-red-600">{error}</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
